import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import apiOffers from 'api/offers';
import IOfferChoice from 'api/interfaces/offers/IOfferChoice';
import IUserGet from 'api/interfaces/users/IUserGet';
import PageContainer from 'components/layout/PageContainer';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import { MANAGE_OFFER_CHOICES } from 'services/enums/users/permissions';
import {
  CONTACT_TITLE,
  INSTALLATION_CATEGORY,
  MATERIAL_CATEGORY,
  OFFER_CHOICE_LISTS,
  OTHER_EXPENSE_CATEGORY,
  PAYMENT_TERM,
  reloadOfferChoices,
  useOfferChoices,
} from 'services/offers/offerChoices';
import hasPermissionType from 'services/users/hasPermissionType';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';

import 'style/offers/OfferChoices.css';

interface IDraft {
  hidden: boolean;
  labelEn: string;
  labelFi: string;
  labelSv: string;
}

const emptyDraft: IDraft = {
  hidden: false,
  labelEn: '',
  labelFi: '',
  labelSv: '',
};

const toDraft = (choice: IOfferChoice): IDraft => ({
  hidden: choice.hidden,
  labelEn: choice.labelEn ?? '',
  labelFi: choice.labelFi ?? '',
  labelSv: choice.labelSv ?? '',
});

const LANGUAGES: { field: 'labelFi' | 'labelSv' | 'labelEn'; key: string }[] = [
  { field: 'labelFi', key: 'Finnish' },
  { field: 'labelSv', key: 'Swedish' },
  { field: 'labelEn', key: 'English' },
];

/**
 * The choices of the dropdown lists of the offers for their admins: a list at a time, the
 * choices in their order with their names in each language. A choice is added, renamed,
 * moved or hidden, never removed, so the offers saved before keep their values.
 */
const OfferChoices = (): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.common]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.offers}:choices.${key}`, options);

  const currentUser = useSelector(
    (state: { users: { current: IUserGet | null } }) => state.users.current
  );
  const mayManage = hasPermissionType(currentUser, MANAGE_OFFER_CHOICES);

  const choices = useOfferChoices();
  const [list, setList] = useState(OFFER_CHOICE_LISTS[0]);
  // The changed names and hidings by choice, not yet saved
  const [drafts, setDrafts] = useState<Record<number, IDraft>>({});
  const [newChoice, setNewChoice] = useState<IDraft>(emptyDraft);
  const [newDays, setNewDays] = useState('');
  const [saving, setSaving] = useState(false);

  const listChoices = choices
    .filter((choice) => choice.list === list)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Another list starts without the changes of the earlier one
  useEffect(() => {
    setDrafts({});
    setNewChoice(emptyDraft);
    setNewDays('');
  }, [list]);

  const draftOf = (choice: IOfferChoice) =>
    drafts[choice.id] ?? toDraft(choice);
  const isChanged = (choice: IOfferChoice) =>
    drafts[choice.id] !== undefined &&
    JSON.stringify(drafts[choice.id]) !== JSON.stringify(toDraft(choice));

  const change = (choice: IOfferChoice, changes: Partial<IDraft>) =>
    setDrafts((current) => ({
      ...current,
      [choice.id]: { ...draftOf(choice), ...changes },
    }));

  const run = async (action: () => Promise<unknown>, message: string) => {
    setSaving(true);
    try {
      await action();
      await reloadOfferChoices();
      dispatch(addMessage(message));
      return true;
    } catch (error) {
      dispatch(addMessage(tr('Save failed')));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = async (choice: IOfferChoice) => {
    const draft = draftOf(choice);
    if (draft.labelFi.trim() === '') {
      dispatch(addMessage(tr('Finnish name required')));
      return;
    }

    const saved = await run(
      () => apiOffers.choices.put(choice.id, draft),
      tr('Saved')
    );
    if (saved) {
      setDrafts((current) => {
        const rest = { ...current };
        delete rest[choice.id];
        return rest;
      });
    }
  };

  // Up or down by one; the order of the whole list is saved
  const move = (index: number, step: number) => {
    const ids = listChoices.map((choice) => choice.id);
    const target = index + step;
    if (target < 0 || target >= ids.length) {
      return;
    }
    [ids[index], ids[target]] = [ids[target], ids[index]];
    run(() => apiOffers.choices.order(list, ids), tr('Order saved'));
  };

  const add = async () => {
    const isPaymentTerm = list === PAYMENT_TERM;
    const days = newDays.trim();
    if (isPaymentTerm && !/^\d+$/.test(days)) {
      dispatch(addMessage(tr('Days required')));
      return;
    }

    // The name of a payment term is its days unless given, e.g. HETI for 0
    const labelFi =
      newChoice.labelFi.trim() === '' && isPaymentTerm
        ? days
        : newChoice.labelFi;
    if (labelFi.trim() === '') {
      dispatch(addMessage(tr('Finnish name required')));
      return;
    }

    const added = await run(
      () =>
        apiOffers.choices.post({
          ...newChoice,
          hidden: false,
          labelFi,
          list,
          value: isPaymentTerm ? days : undefined,
        }),
      tr('Added')
    );
    if (added) {
      setNewChoice(emptyDraft);
      setNewDays('');
    }
  };

  if (!mayManage) {
    return (
      <PageContainer>
        <h1>{tr('Title')}</h1>
        <p>{tr('No permission')}</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <h1>{tr('Title')}</h1>
      <p>{tr('Info')}</p>
      <div className="offer-choices">
        {/* The lists at the left, the choices of the chosen one at the right */}
        <nav className="offer-choices-lists">
          {OFFER_CHOICE_LISTS.map((name) => (
            <button
              className={`offer-choices-list ${name === list ? 'active' : ''}`}
              key={name}
              onClick={() => setList(name)}
              type="button"
            >
              {tr(`lists.${name}`)}
              <span className="offer-choices-count">
                {
                  choices.filter(
                    (choice) => choice.list === name && !choice.hidden
                  ).length
                }
              </span>
            </button>
          ))}
        </nav>
        <section className="offer-choices-panel">
          <h2>{tr(`lists.${list}`)}</h2>
          {list === PAYMENT_TERM ? (
            <p className="offer-choices-info">{tr('Payment term info')}</p>
          ) : null}
          {list === CONTACT_TITLE ? (
            <p className="offer-choices-info">{tr('Contact title info')}</p>
          ) : null}
          {[
            MATERIAL_CATEGORY,
            INSTALLATION_CATEGORY,
            OTHER_EXPENSE_CATEGORY,
          ].includes(list) ? (
            <p className="offer-choices-info">{tr('Category info')}</p>
          ) : null}
          <div className="offer-choices-grid offer-choices-header">
            <span>{tr('Order')}</span>
            {LANGUAGES.map((language) => (
              <span key={language.field}>{tr(language.key)}</span>
            ))}
            <span>{tr('Hidden')}</span>
            <span />
          </div>
          {listChoices.map((choice, index) => {
            const draft = draftOf(choice);
            return (
              <div
                className={`offer-choices-grid offer-choices-row ${
                  draft.hidden ? 'hidden-choice' : ''
                }`}
                key={choice.id}
              >
                <div className="offer-choices-order">
                  <button
                    aria-label={tr('Up')}
                    disabled={saving || index === 0}
                    onClick={() => move(index, -1)}
                    title={tr('Up')}
                    type="button"
                  >
                    ▲
                  </button>
                  <button
                    aria-label={tr('Down')}
                    disabled={saving || index === listChoices.length - 1}
                    onClick={() => move(index, 1)}
                    title={tr('Down')}
                    type="button"
                  >
                    ▼
                  </button>
                </div>
                {LANGUAGES.map((language) => (
                  <Input
                    aria-label={tr(language.key)}
                    key={language.field}
                    onChange={(event) =>
                      change(choice, { [language.field]: event.target.value })
                    }
                    title={`${tr('Code')}: ${choice.value}`}
                    value={draft[language.field]}
                  />
                ))}
                <div
                  className="offer-choices-hidden"
                  title={choice.locked ? tr('Locked') : tr('Hidden info')}
                >
                  <input
                    aria-label={tr('Hidden')}
                    checked={draft.hidden}
                    disabled={choice.locked}
                    onChange={(event) =>
                      change(choice, { hidden: event.target.checked })
                    }
                    type="checkbox"
                  />
                  {choice.locked ? (
                    <span className="offer-choices-locked">{tr('Fixed')}</span>
                  ) : null}
                </div>
                <PrimaryButton
                  disabled={saving || !isChanged(choice)}
                  onClick={() => save(choice)}
                >
                  {t(`${translations.common}:Save`)}
                </PrimaryButton>
              </div>
            );
          })}
          {/* A new choice at the end of the list */}
          <div className="offer-choices-grid offer-choices-new">
            <span className="offer-choices-new-title">
              {list === PAYMENT_TERM ? (
                <Input
                  aria-label={tr('Days')}
                  inputMode="numeric"
                  onChange={(event) => setNewDays(event.target.value)}
                  placeholder={tr('Days')}
                  value={newDays}
                />
              ) : (
                tr('New')
              )}
            </span>
            {LANGUAGES.map((language) => (
              <Input
                aria-label={tr(language.key)}
                key={language.field}
                onChange={(event) =>
                  setNewChoice({
                    ...newChoice,
                    [language.field]: event.target.value,
                  })
                }
                placeholder={tr(language.key)}
                value={newChoice[language.field]}
              />
            ))}
            <span />
            <PrimaryButton disabled={saving} onClick={add}>
              {`+ ${tr('Add')}`}
            </PrimaryButton>
          </div>
          <p className="offer-choices-info">{tr('Never removed')}</p>
        </section>
      </div>
    </PageContainer>
  );
};

export default OfferChoices;
